// src/pages/GuestBillingPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import CheckOutInvoice from "../components/CheckOutInvoice"; // reused as a generic bill preview
import EditGuestBillModal from "./EditGuestBillModal";

import { Alert, Button, Form, Input, InputNumber, Modal, Table, Typography } from "antd";
import {
  AmountRows,
  CardTitle,
  DetailList,
  PageHeader,
  RowActions,
  Section,
  SectionCard,
  StatStrip,
  StatusTag,
  fmtDateTime,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import { IconInfo, IconPlus, IconRefresh, IconSearch } from "../components/ui/icons";
import notify from "../utils/notify";

const { Text } = Typography;

/* ---------- helpers ---------- */
const asNum = (v) => Number(v ?? 0);
const fmtMoney = (n) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(asNum(n));

/* ============================================================
   GuestBillingPage (F&B / POS usage)
   - Search active guest by room number
   - Add ad-hoc charges and save bill
   - List + edit guest bills
   - Apply payments to bills
   - Preview/print bill (no checkout logic)
============================================================ */
const GuestBillingPage = () => {
  useFlatPage();
  const { authData, logout } = useAuth();

  /* search / active guest */
  const [roomNumberSearch, setRoomNumberSearch] = useState("");
  const [activeGuest, setActiveGuest] = useState(null);
  const [guestError, setGuestError] = useState("");

  /* new charge form */
  const [form] = Form.useForm();
  const [billingItems, setBillingItems] = useState([]);

  /* save bill */
  const [savingBill, setSavingBill] = useState(false);

  /* existing bills */
  const [billings, setBillings] = useState([]);
  const [isEditBillModalOpen, setIsEditBillModalOpen] = useState(false);
  const [billToEdit, setBillToEdit] = useState(null);

  /* payments update map (billingId -> amount to add) */
  const [paymentUpdates, setPaymentUpdates] = useState({});

  /* invoice/bill preview modal */
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  /* info modal */
  const [showInfoModal, setShowInfoModal] = useState(false); // <- added

  /* hotel header for bill */
  const [hotelDetails, setHotelDetails] = useState({
    hotelName: "",
    address: "",
    phoneNumber: "",
  });

  /* derived totals for new charge set */
  const [vatPercentage, setVatPercentage] = useState(0); // taken from your room prices; 0 means this hotel does not charge VAT
  const subtotal = useMemo(
    () => billingItems.reduce((acc, it) => acc + asNum(it.amount), 0),
    [billingItems]
  );
  const vatAmount = useMemo(() => subtotal * (vatPercentage / 100), [subtotal, vatPercentage]);
  const totalAmount = useMemo(() => subtotal + vatAmount, [subtotal, vatAmount]);
  const totalPaid = useMemo(
    () => billingItems.reduce((acc, it) => acc + asNum(it.paidAmount), 0),
    [billingItems]
  );

  /* auth guard / hotel fetch */
  useEffect(() => {
    if (!authData?.hotelId) {
      setGuestError("User authentication is missing. Please log in.");
      logout();
      return;
    }
    (async () => {
      try {
        const res = await axiosInstance.get(`/Hotel/${authData.hotelId}`);
        setHotelDetails({
          hotelName: res.data.hotelName || "Hotel Name",
          address: res.data.address || "Hotel Address",
          phoneNumber: res.data.contactNumber || "+000 000 0000",
        });
      } catch {
        // non-fatal
      }
    })();
  }, [authData?.hotelId, logout]);

  /* VAT is optional: use the rate set on the hotel's active room prices, or none */
  useEffect(() => {
    if (!authData?.hotelId) return;
    (async () => {
      try {
        const res = await axiosInstance.get(`/Price/hotel/${authData.hotelId}`);
        const rates = (Array.isArray(res.data) ? res.data : [])
          .filter((p) => (p.isActive ?? p.IsActive) !== false)
          .map((p) => Number(p.vatPercentage ?? p.VatPercentage ?? 0))
          .filter((n) => Number.isFinite(n) && n > 0);
        setVatPercentage(rates.length ? Math.max(...rates) : 0);
      } catch {
        setVatPercentage(0);
      }
    })();
  }, [authData?.hotelId]);

  /* main: search guest by room */
  const fetchGuestByRoomNumber = async () => {
    if (!roomNumberSearch.trim()) {
      setGuestError("Please enter a room number.");
      return;
    }
    try {
      setGuestError("");
      const res = await axiosInstance.get("/GuestBillings/activeGuest", {
        params: { hotelId: authData.hotelId, roomNumber: roomNumberSearch.trim() },
      });
      const guestBooking = res.data;
      if (guestBooking) {
        setActiveGuest(guestBooking);
        await fetchBillings(guestBooking.GuestId || guestBooking.guestId);
      } else {
        setActiveGuest(null);
        setBillings([]);
        setGuestError("No active guest found for this room.");
      }
    } catch {
      setActiveGuest(null);
      setBillings([]);
      setGuestError("Failed to fetch guest details. Please try again.");
    }
  };

  const fetchBillings = async (guestId) => {
    if (!guestId) return;
    try {
      const res = await axiosInstance.get(`/GuestBillings/guest/${guestId}`, {
        params: { hotelId: authData.hotelId },
      });
      setBillings(Array.isArray(res.data) ? res.data : []);
    } catch {
      // non-fatal
    }
  };

  /* reset everything */
  const resetAll = () => {
    setRoomNumberSearch("");
    setActiveGuest(null);
    setBillings([]);
    setBillingItems([]);
    form.resetFields();
    setPaymentUpdates({});
    setGuestError("");
  };

  /* add one item to the "new charges" list */
  const handleAddBillingItem = (values) => {
    const { description, amount, paidAmount } = values;
    setBillingItems((prev) => [
      ...prev,
      {
        _key: `${Date.now()}-${Math.random()}`,
        description: description.trim(),
        amount: asNum(amount),
        paidAmount: asNum(paidAmount || 0),
      },
    ]);
    form.resetFields(["description", "amount", "paidAmount"]);
  };

  /* save guest bill (combines all staged charge rows) */
  const handleSaveGuestBill = async () => {
    if (!activeGuest) {
      notify.error("No active guest selected.");
      return;
    }
    if (billingItems.length === 0) {
      notify.error("Please add at least one billing item.");
      return;
    }

    const combinedDescription = billingItems.map((it) => it.description).join(", ");
    const combinedPaid = billingItems.reduce((acc, it) => acc + asNum(it.paidAmount), 0);

    const payload = {
      guestId: activeGuest.GuestId || activeGuest.guestId,
      hotelId: authData.hotelId,
      RoomNumber: activeGuest.roomNumber,
      BookingNo: activeGuest.bookingNo || "DEFAULT_VALUE",
      BookingId: activeGuest.bookingId || 0,
      description: combinedDescription,
      amount: totalAmount,
      paidAmount: combinedPaid,
      status: combinedPaid >= totalAmount ? "Paid" : "Pending",
      billedBy: `${authData.firstName} ${authData.lastName}`,
    };

    try {
      setSavingBill(true);
      await axiosInstance.post(`/GuestBillings/${authData.hotelId}`, payload);
      notify.success({ title: "Guest bill saved" });
      setBillingItems([]);
      await fetchBillings(activeGuest.GuestId || activeGuest.guestId);
    } catch (e) {
      notify.error({
        title: "Failed to save guest bill",
        description: e?.response?.data?.message || "Please try again.",
      });
    } finally {
      setSavingBill(false);
    }
  };

  /* open edit modal for one billing row */
  const handleEditBill = (bill) => {
    setBillToEdit(bill);
    setIsEditBillModalOpen(true);
  };

  /* update payment inputs per bill row (stored in paymentUpdates) */
  const setPaymentFor = (billingId, value) => {
    setPaymentUpdates((prev) => ({ ...prev, [billingId]: value }));
  };

  /* apply payments only (no checkout!) */
  const applyPayments = async () => {
    if (!activeGuest) return;

    const updates = Object.entries(paymentUpdates).filter(
      ([, val]) => asNum(val) > 0
    );
    if (updates.length === 0) {
      notify.info("No payment amounts entered.");
      return;
    }

    try {
      await Promise.all(
        billings.map((b) => {
          const add = asNum(paymentUpdates[b.billingId]);
          if (add > 0) {
            const newPaid = asNum(b.PaidAmount ?? b.paidAmount ?? 0) + add;
            return axiosInstance.put(`/GuestBillings/${b.billingId}`, {
              GuestId: b.GuestId,
              HotelId: authData.hotelId,
              RoomNumber: b.RoomNumber || activeGuest.roomNumber,
              BookingNo: b.bookingNo || activeGuest.bookingNo,
              Description: b.description,
              Amount: b.amount,
              PaidAmount: newPaid,
              BilledBy: b.billedBy,
            });
          }
          return Promise.resolve();
        })
      );

      setPaymentUpdates({});
      await fetchBillings(activeGuest.GuestId || activeGuest.guestId);
      notify.success({ message: "Payments applied successfully." });
    } catch (e) {
      notify.error({
        title: "Failed to apply payments",
        description: e?.response?.data?.message || "Please try again.",
      });
    }
  };

  /* open bill preview (reusing CheckOutInvoice as a generic invoice view) */
  const openBillPreview = () => {
    if (!activeGuest) return;
    const mapped = {
      guestDetails: {
        name: `${activeGuest.firstName ?? ""} ${activeGuest.lastName ?? ""}`.trim() || "N/A",
        bookingNo: activeGuest.bookingNo || "N/A",
        roomNumber: activeGuest.roomNumber || "N/A",
        paymentAmount: 0,
      },
      billingRecords: billings || [],
    };
    setInvoiceDetails(mapped);
    setShowInvoiceModal(true);
  };

  /* tables */
  const newItemsColumns = [
    { title: "Description", dataIndex: "description", key: "desc", render: (v) => <span className="hms-cell-main">{v}</span> },
    { title: "Amount", dataIndex: "amount", key: "amt", align: "right", render: (v) => fmtMoney(v) },
    { title: "Paid", dataIndex: "paidAmount", key: "paid", align: "right", render: (v) => fmtMoney(v) },
  ];

  const billingColumns = [
    {
      title: "Bill",
      key: "bill",
      render: (_, r) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{r.description || "—"}</span>
          <span className="hms-cell-sub">
            #{r.billingId} · {r.createdDate ? fmtDateTime(r.createdDate) : "—"} · {r.billedBy || "—"}
          </span>
        </div>
      ),
    },
    { title: "Amount", dataIndex: "amount", align: "right", width: 120, render: (v) => fmtMoney(v) },
    { title: "Paid", dataIndex: "PaidAmount", align: "right", width: 120, render: (_, r) => fmtMoney(r.PaidAmount ?? r.paidAmount ?? 0) },
    {
      title: "Owed",
      dataIndex: "amountOwed",
      align: "right",
      width: 120,
      render: (v, r) => {
        const owed = v ?? Math.max(0, asNum(r.amount) - asNum(r.PaidAmount ?? r.paidAmount));
        return <span className={asNum(owed) > 0 ? "hms-cell-main" : "hms-muted"}>{fmtMoney(owed)}</span>;
      },
    },
    {
      title: "Pay now",
      key: "paynow",
      width: 170,
      render: (_, r) => (
        <InputNumber
          min={0}
          style={{ width: "100%" }}
          placeholder="Amount"
          value={paymentUpdates[r.billingId] ?? 0}
          onChange={(val) => setPaymentFor(r.billingId, asNum(val))}
        />
      ),
    },
    {
      title: "",
      width: 70,
      align: "right",
      render: (_, r) => <RowActions actions={[{ key: "edit", label: "Edit bill", icon: "edit", onClick: () => handleEditBill(r) }]} />,
    },
  ];

  const pending = asNum(activeGuest?.pendingAmount);

  return (
    <div className="hms-page gb-page">
      <PageHeader
        actions={
          <>
            <Button icon={<IconInfo />} onClick={() => setShowInfoModal(true)}>
              Guide
            </Button>
            <Button icon={<IconRefresh />} onClick={resetAll}>
              Reset
            </Button>
          </>
        }
      />

      <div className="hms-stack">
        <SectionCard title="Find a guest">
          <div className="hms-form-actions" style={{ justifyContent: "flex-start", marginTop: 0 }}>
            <Input
              allowClear
              style={{ width: 280 }}
              placeholder="Room number"
              value={roomNumberSearch}
              onChange={(e) => setRoomNumberSearch(e.target.value)}
              onPressEnter={fetchGuestByRoomNumber}
              prefix={<IconSearch />}
            />
            <Button type="primary" onClick={fetchGuestByRoomNumber}>
              Find guest
            </Button>
          </div>
          {!!guestError && <Alert type="error" showIcon title={guestError} style={{ marginTop: 12 }} />}
        </SectionCard>

        {!activeGuest ? (
          <SectionCard>
            <div className="hms-empty">Search for an active guest by room number to start billing.</div>
          </SectionCard>
        ) : (
          <>
            <SectionCard
              flush
              title={`${activeGuest.firstName ?? ""} ${activeGuest.lastName ?? ""}`.trim() || "Active guest"}
              extra={
                <div className="hms-form-actions" style={{ marginTop: 0 }}>
                  <Button onClick={openBillPreview}>Bill preview</Button>
                  <Button type="primary" onClick={applyPayments}>
                    Apply payments
                  </Button>
                </div>
              }
            >
              <StatStrip
                items={[
                  { label: "Room folio balance", value: fmtMoney(pending), tone: pending > 0 ? "warn" : "good" },
                  { label: "Charges (staged)", value: fmtMoney(totalAmount) },
                  { label: "Room", value: activeGuest.roomNumber || "—" },
                  { label: "Booking", value: activeGuest.bookingNo || "—" },
                ]}
              />
              <div style={{ padding: "4px 20px 12px" }}>
                <DetailList
                  items={[
                    { label: "Guest ID", value: activeGuest.GuestId || activeGuest.guestId },
                    { label: "ID number", value: activeGuest.idNumber },
                    {
                      label: "Folio status",
                      value: <StatusTag status={pending > 0 ? "pending" : "available"}>{pending > 0 ? "Pending" : "Clear"}</StatusTag>,
                    },
                  ]}
                />
              </div>
            </SectionCard>

            <SectionCard title="Additional charges" extra={<span className="hms-pill">{vatPercentage > 0 ? `VAT ${vatPercentage}%` : "No VAT"}</span>}>
              <Form form={form} layout="inline" onFinish={handleAddBillingItem} style={{ rowGap: 10 }}>
                <Form.Item name="description" rules={[{ required: true, message: "Item description is required" }]}>
                  <Input placeholder="Food, drinks, laundry, spa..." style={{ width: 260 }} />
                </Form.Item>
                <Form.Item name="amount" rules={[{ required: true, message: "Amount is required" }]}>
                  <InputNumber min={0.01} step={0.01} placeholder="Amount" style={{ width: 140 }} />
                </Form.Item>
                <Form.Item name="paidAmount">
                  <InputNumber min={0} step={0.01} placeholder="Paid (optional)" style={{ width: 160 }} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" icon={<IconPlus />}>
                    Add
                  </Button>
                </Form.Item>
              </Form>

              {billingItems.length === 0 ? (
                <div className="hms-empty">No items added yet</div>
              ) : (
                <>
                  <Table
                    className="hms-table"
                    style={{ marginTop: 12 }}
                    rowKey="_key"
                    columns={newItemsColumns}
                    dataSource={billingItems}
                    pagination={false}
                  />
                  <div style={{ maxWidth: 320, marginLeft: "auto", marginTop: 12 }}>
                    <AmountRows
                      rows={[
                        { label: "Subtotal", value: fmtMoney(subtotal) },
                        vatPercentage > 0 ? { label: `VAT (${vatPercentage}%)`, value: fmtMoney(vatAmount) } : null,
                        { divider: true },
                        { label: "Total", value: fmtMoney(totalAmount), strong: true },
                      ]}
                    />
                  </div>
                  <div className="hms-form-actions">
                    <Button
                      onClick={() => {
                        setBillingItems([]);
                        form.resetFields(["description", "amount", "paidAmount"]);
                      }}
                    >
                      Clear
                    </Button>
                    <Button type="primary" onClick={handleSaveGuestBill} loading={savingBill}>
                      Save guest bill
                    </Button>
                  </div>
                </>
              )}
            </SectionCard>

            <SectionCard flush title={<CardTitle count={billings.length}>Billing records</CardTitle>}>
              {billings.length === 0 ? (
                <div className="hms-empty">No billing records found</div>
              ) : (
                <>
                  <Table
                    className="hms-table"
                    rowKey="billingId"
                    columns={billingColumns}
                    dataSource={billings}
                    pagination={tablePagination("bill", { hideOnSinglePage: true })}
                    scroll={{ x: 800 }}
                  />
                  <p className="hms-card-hint" style={{ padding: "0 20px 14px" }}>
                    Enter a "Pay now" amount on each row, then click "Apply payments" at the top to post them.
                  </p>
                </>
              )}
            </SectionCard>
          </>
        )}
      </div>

      {/* Edit bill modal */}
      {isEditBillModalOpen && billToEdit && (
        <EditGuestBillModal
          isOpen={isEditBillModalOpen}
          billing={billToEdit}
          onClose={() => setIsEditBillModalOpen(false)}
          onBillUpdated={() => activeGuest && fetchBillings(activeGuest.GuestId || activeGuest.guestId)}
        />
      )}

      {/* Bill preview (reuses the shared invoice) */}
      {showInvoiceModal && invoiceDetails && (
        <CheckOutInvoice
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          guestDetails={invoiceDetails.guestDetails}
          hotelDetails={hotelDetails}
          billingRecords={invoiceDetails.billingRecords}
        />
      )}

      <Modal
        title="How to use Guest Billing"
        open={showInfoModal}
        onCancel={() => setShowInfoModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfoModal(false)}>
            Got it
          </Button>,
        ]}
        width={600}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          title="This page is for kitchen, restaurant and bar billing. It does not check guests out."
          style={{ marginBottom: 16 }}
        />

        <Section title="Workflow">
          <ol style={{ paddingLeft: 20, margin: 0 }}>
            <li>Find the guest by room number.</li>
            <li>Add items under Additional charges. VAT is added only if your room prices have a VAT rate.</li>
            <li>Click Save guest bill to post the items as one bill.</li>
            <li>Use Billing records to view or edit past bills and record payments with Pay now, then Apply payments.</li>
            <li>Use Bill preview for a printable copy for the guest.</li>
          </ol>
        </Section>

        <Section title="Good to know">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Room folio balance is the accommodation balance from the front desk. It is read-only here.</li>
            <li>The VAT rate is taken from your room prices. If you do not charge VAT, none is added.</li>
            <li>Each saved bill records the signed-in staff member as "billed by".</li>
            <li>If a guest can't be found, check the room number and that the guest is checked in.</li>
          </ul>
        </Section>
      </Modal>
    </div>
  );
};

export default GuestBillingPage;
